/**
 * SyncLibroMatricolaTab — second tab of ImportDialog.
 *
 * Workflow:
 *   1. Admin uploads the .xlsx "Libro Matricola" → POST /api/employees/sync-libro-matricola?mode=preview
 *   2. UI shows per-sheet diff (to_create / to_update / unchanged / parsing_errors).
 *   3. Admin reviews and clicks "Applica modifiche" → same endpoint with mode=apply.
 *   4. Result banner + list refresh callback.
 *
 * Empty Excel cells preserve DB values (backend-enforced).
 */
import { useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "./ui/accordion";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import {
    Upload,
    FileSpreadsheet,
    Loader2,
    CheckCircle2,
    AlertTriangle,
    UserPlus,
    Pencil,
    Check,
    RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

export const SyncLibroMatricolaTab = ({ onSynced }) => {
    const [file, setFile] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [report, setReport] = useState(null);
    const [applying, setApplying] = useState(false);
    const [applyResult, setApplyResult] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [applyAttivi, setApplyAttivi] = useState(true);
    const [applyCessati, setApplyCessati] = useState(true);
    const [decisions, setDecisions] = useState({});
    const inputRef = useRef(null);

    const reset = () => {
        setFile(null);
        setReport(null);
        setApplyResult(null);
        setDecisions({});
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.name.toLowerCase().match(/\.xlsx?$/)) {
            toast.error("Carica un file Excel (.xlsx)");
            return;
        }
        setFile(f);
        setReport(null);
        setApplyResult(null);
        setDecisions({});
    };

    const setDecision = (fileRow, action) => {
        setDecisions((prev) => {
            const next = { ...prev };
            if (action === fileRow.default_action) delete next[fileRow.row_key];
            else next[fileRow.row_key] = action;
            return next;
        });
    };

    const overridesCount = Object.keys(decisions).length;

    const runPreview = async () => {
        if (!file) return;
        setPreviewing(true);
        setReport(null);
        setApplyResult(null);
        setDecisions({});
        const fd = new FormData();
        fd.append("file", file);
        fd.append("mode", "preview");
        try {
            const { data } = await api.post("/employees/sync-libro-matricola", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            setReport(data);
            const totA = (data.attivi?.to_create.length || 0) + (data.attivi?.to_update.length || 0);
            const totC = (data.cessati?.to_create.length || 0) + (data.cessati?.to_update.length || 0);
            if (totA === 0 && totC === 0) {
                toast.success("Tutti i dati sono già sincronizzati");
            } else {
                toast.success(`Anteprima pronta: ${totA + totC} modifiche identificate`);
            }
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "Errore durante l'anteprima";
            toast.error(msg);
        } finally {
            setPreviewing(false);
        }
    };

    const runApply = async () => {
        if (!file) return;
        setApplying(true);
        setConfirmOpen(false);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("mode", "apply");
        fd.append("apply_attivi", String(applyAttivi));
        fd.append("apply_cessati", String(applyCessati));
        if (overridesCount > 0) {
            fd.append("homonym_decisions", JSON.stringify(decisions));
        }
        try {
            const { data } = await api.post("/employees/sync-libro-matricola", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            setApplyResult(data);
            const total = (data.applied_created || 0) + (data.applied_updated || 0);
            toast.success(
                `Sincronizzazione completata: ${data.applied_created} creati, ${data.applied_updated} aggiornati${total === 0 ? " (nessuna modifica)" : ""}`,
                { duration: 4000 },
            );
            if (data.apply_errors?.length) {
                toast.warning(`${data.apply_errors.length} errori (vedi report sotto)`);
            }
            onSynced && onSynced();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore durante l'applicazione");
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="sync-libro-matricola-tab">
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-5 text-center">
                <FileSpreadsheet className="mx-auto h-9 w-9 text-slate-400" />
                <div className="mt-2 font-display text-sm font-semibold text-[#376DB3]">
                    {file ? file.name : "Carica il file Libro Matricola (.xlsx)"}
                </div>
                {file && (
                    <div className="mt-0.5 text-xs text-slate-500">
                        {(file.size / 1024).toFixed(1)} KB
                    </div>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,.xlsm"
                    className="hidden"
                    onChange={handleFile}
                    data-testid="sync-file-input"
                />
                <div className="mt-3 flex justify-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => inputRef.current?.click()}
                        data-testid="sync-file-pick"
                        disabled={previewing || applying}
                    >
                        <Upload className="mr-2 h-4 w-4" />
                        {file ? "Cambia file" : "Seleziona file"}
                    </Button>
                    <Button
                        size="sm"
                        onClick={runPreview}
                        disabled={!file || previewing || applying}
                        className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="sync-preview-btn"
                    >
                        {previewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                        {previewing ? "Genero anteprima…" : "Genera anteprima"}
                    </Button>
                </div>
            </div>

            <div className="rounded-md bg-blue-50 px-3 py-2 text-[11px] leading-relaxed text-blue-900">
                Legge i fogli <b>ATTIVI</b> e <b>CESSATI</b> (case-insensitive) e li confronta con il database per <b>matricola</b>.
                Le celle vuote nel file Excel <b>non sovrascrivono</b> i valori esistenti nel database.
            </div>

            {report && (
                <>
                    {report.homonym_warnings?.length > 0 && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="sync-homonym-warnings">
                            <div className="text-xs font-bold uppercase tracking-wide text-amber-800">
                                ⚠ Possibili omonimie rilevate ({report.homonym_warnings.length})
                            </div>
                            <p className="mt-1 text-xs text-amber-800">
                                Questi nomi compaiono con codici fiscali diversi (persone distinte con lo stesso nome
                                o errori di battitura nel CF). Per ogni riga del file puoi scegliere se
                                <b> importarla</b> o <b>ignorarla</b>: la scelta viene eseguita quando premi "Applica modifiche".
                            </p>
                            <div className="mt-2 max-h-80 space-y-1.5 overflow-y-auto">
                                {report.homonym_warnings.map((w) => (
                                    <div key={w.name} className="rounded bg-white/70 px-2 py-1.5 text-xs" data-testid={`homonym-${w.name.replace(/\s+/g, "-")}`}>
                                        <span className="font-semibold text-slate-800">{w.name}</span>
                                        <ul className="mt-0.5 space-y-0.5 text-slate-600">
                                            {w.profiles.map((p) => (
                                                <li key={p.codice_fiscale}>
                                                    <span className="font-mono">{p.codice_fiscale}</span>
                                                    {" — "}{p.sources.join(", ")}
                                                </li>
                                            ))}
                                        </ul>
                                        {w.file_rows?.length > 0 && (
                                            <div className="mt-1.5 space-y-1 border-t border-amber-200/60 pt-1.5">
                                                {w.file_rows.map((fr) => {
                                                    const current = decisions[fr.row_key] ?? fr.default_action;
                                                    return (
                                                        <div
                                                            key={fr.row_key}
                                                            className="rounded border border-slate-200 bg-white px-2 py-1.5"
                                                            data-testid={`homonym-row-${fr.row_key.replace(":", "-")}`}
                                                        >
                                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                                <span className={`rounded px-1 py-0.5 text-[9px] font-bold uppercase ${fr.sheet === "attivi" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                                                                    {fr.sheet}
                                                                </span>
                                                                <span className="font-mono-numbers text-slate-600">matr. {fr.matricola}</span>
                                                                {fr.codice_fiscale && (
                                                                    <span className="font-mono text-[10px] text-slate-500">{fr.codice_fiscale}</span>
                                                                )}
                                                                {fr.data_cessazione && (
                                                                    <span className="text-[10px] font-semibold text-red-600">cess. {fr.data_cessazione}</span>
                                                                )}
                                                                <span className="ml-auto inline-flex overflow-hidden rounded-md border border-slate-300">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setDecision(fr, "skip")}
                                                                        className={`px-2 py-0.5 text-[10px] font-semibold transition-colors ${current === "skip" ? "bg-slate-700 text-white" : "bg-white text-slate-500 hover:bg-slate-100"}`}
                                                                        data-testid={`homonym-skip-${fr.row_key.replace(":", "-")}`}
                                                                    >
                                                                        Ignora
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setDecision(fr, "import")}
                                                                        className={`px-2 py-0.5 text-[10px] font-semibold transition-colors ${current === "import" ? "bg-emerald-600 text-white" : "bg-white text-slate-500 hover:bg-slate-100"}`}
                                                                        data-testid={`homonym-import-${fr.row_key.replace(":", "-")}`}
                                                                    >
                                                                        Importa
                                                                    </button>
                                                                </span>
                                                            </div>
                                                            <div className="mt-0.5 text-[10px] leading-snug text-slate-500">
                                                                {current === "import" ? (
                                                                    <span className="text-emerald-700">→ {fr.will_do_if_imported}</span>
                                                                ) : (
                                                                    <span>Ignorata: {fr.reason}</span>
                                                                )}
                                                                {decisions[fr.row_key] && (
                                                                    <span className="ml-1 rounded bg-amber-100 px-1 py-px text-[9px] font-bold uppercase text-amber-800">scelta manuale</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {overridesCount > 0 && (
                                <div className="mt-2 rounded bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-900" data-testid="homonym-overrides-count">
                                    {overridesCount} {overridesCount === 1 ? "scelta manuale attiva" : "scelte manuali attive"} — verranno eseguite con "Applica modifiche"
                                </div>
                            )}
                        </div>
                    )}
                    <SheetSummary report={report.attivi} testidPrefix="sync-attivi" />
                    {report.cessati && (
                        <SheetSummary report={report.cessati} testidPrefix="sync-cessati" />
                    )}

                    <div className="flex flex-col items-stretch gap-2 rounded-md border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-xs text-slate-600">
                            <label className="mr-3 inline-flex items-center gap-1">
                                <input
                                    type="checkbox"
                                    checked={applyAttivi}
                                    onChange={(e) => setApplyAttivi(e.target.checked)}
                                    data-testid="sync-apply-attivi-toggle"
                                />
                                <span>Applica modifiche ATTIVI</span>
                            </label>
                            {report.cessati && (
                                <label className="inline-flex items-center gap-1">
                                    <input
                                        type="checkbox"
                                        checked={applyCessati}
                                        onChange={(e) => setApplyCessati(e.target.checked)}
                                        data-testid="sync-apply-cessati-toggle"
                                    />
                                    <span>Applica modifiche CESSATI</span>
                                </label>
                            )}
                        </div>
                        <Button
                            onClick={() => setConfirmOpen(true)}
                            disabled={applying || (!applyAttivi && !applyCessati)}
                            className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
                            data-testid="sync-apply-btn"
                        >
                            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            Applica modifiche
                        </Button>
                    </div>
                </>
            )}

            {applyResult && (
                <div className="space-y-2" data-testid="sync-apply-result">
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
                        <div className="flex items-center gap-2 font-semibold text-emerald-900">
                            <CheckCircle2 className="h-4 w-4" /> Sincronizzazione completata
                        </div>
                        <div className="mt-1 text-xs text-emerald-800">
                            <b>{applyResult.applied_created}</b> dipendenti creati ·{" "}
                            <b>{applyResult.applied_updated}</b> aggiornati
                        </div>
                    </div>
                    {applyResult.apply_errors?.length > 0 && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                            <div className="flex items-center gap-2 font-semibold">
                                <AlertTriangle className="h-4 w-4" /> {applyResult.apply_errors.length} errori
                            </div>
                            <div className="mt-1 max-h-32 overflow-y-auto">
                                {applyResult.apply_errors.slice(0, 30).map((err, i) => (
                                    <div key={`${err.sheet}-${err.matricola}-${i}`} className="border-b border-amber-200 py-0.5 last:border-b-0">
                                        <span className="font-mono-numbers">[{err.sheet}/{err.action}]</span>{" "}
                                        <b>{err.matricola}</b>: {err.error}
                                    </div>
                                ))}
                                {applyResult.apply_errors.length > 30 && (
                                    <div className="pt-1 font-italic">… +{applyResult.apply_errors.length - 30} altri</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="sync-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confermi la sincronizzazione?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-1 text-sm">
                                <div>Stai per applicare le seguenti modifiche al database:</div>
                                {applyAttivi && report?.attivi && (
                                    <div className="rounded-md bg-slate-50 px-2 py-1">
                                        <b>ATTIVI:</b> {report.attivi.to_create.length} creazioni, {report.attivi.to_update.length} aggiornamenti
                                    </div>
                                )}
                                {applyCessati && report?.cessati && (
                                    <div className="rounded-md bg-slate-50 px-2 py-1">
                                        <b>CESSATI:</b> {report.cessati.to_create.length} creazioni, {report.cessati.to_update.length} aggiornamenti
                                    </div>
                                )}
                                <div className="pt-1 text-xs text-slate-500">
                                    L'operazione non è reversibile automaticamente.
                                </div>
                                {overridesCount > 0 && (
                                    <div className="rounded-md bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800">
                                        + {overridesCount} {overridesCount === 1 ? "scelta manuale sulle omonimie" : "scelte manuali sulle omonimie"}
                                    </div>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="sync-confirm-cancel">Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runApply}
                            className="bg-emerald-600 text-white hover:bg-emerald-700"
                            data-testid="sync-confirm-apply"
                        >
                            Applica
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

const SheetSummary = ({ report, testidPrefix }) => {
    if (!report) return null;
    const isError = report.rows_total === 0 && report.parsing_errors.length > 0;
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={testidPrefix}>
            <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold uppercase tracking-wide text-[#376DB3]">
                    {report.sheet}
                </div>
                <div className="text-[11px] text-slate-500">
                    {report.rows_total} righe lette
                </div>
            </div>
            {isError ? (
                <div className="text-xs text-amber-700">
                    Nessuna riga valida nel foglio
                </div>
            ) : (
                <div className="flex flex-wrap gap-2 text-[11px]">
                    <Badge color="emerald" icon={<UserPlus className="h-3 w-3" />}>
                        {report.to_create.length} da creare
                    </Badge>
                    <Badge color="amber" icon={<Pencil className="h-3 w-3" />}>
                        {report.to_update.length} da aggiornare
                    </Badge>
                    <Badge color="slate" icon={<Check className="h-3 w-3" />}>
                        {report.unchanged} invariati
                    </Badge>
                    {report.rows_skipped_no_matricola > 0 && (
                        <Badge color="slate" icon={null}>
                            {report.rows_skipped_no_matricola} senza matricola valida
                        </Badge>
                    )}
                    {report.rows_skipped_duplicate_in_sheet > 0 && (
                        <Badge color="slate" icon={null}>
                            {report.rows_skipped_duplicate_in_sheet} duplicati nel foglio
                        </Badge>
                    )}
                    {report.rows_skipped_present_in_attivi > 0 && (
                        <Badge color="slate" icon={null}>
                            {report.rows_skipped_present_in_attivi} riassunti protetti (presenti tra gli ATTIVI)
                        </Badge>
                    )}
                    {report.rows_skipped_homonym_attivi > 0 && (
                        <Badge color="slate" icon={null}>
                            {report.rows_skipped_homonym_attivi} omonimi di ATTIVI ignorati
                        </Badge>
                    )}
                    {report.rows_skipped_not_active_in_portale > 0 && (
                        <Badge color="slate" icon={null}>
                            {report.rows_skipped_not_active_in_portale} ignorati (nessun dipendente attivo corrispondente)
                        </Badge>
                    )}
                    {report.rows_skipped_by_admin > 0 && (
                        <Badge color="amber" icon={null}>
                            {report.rows_skipped_by_admin} esclusi manualmente
                        </Badge>
                    )}
                </div>
            )}

            <Accordion type="multiple" className="mt-2">
                {report.to_create.length > 0 && (
                    <AccordionItem value="create" className="border-b-0">
                        <AccordionTrigger className="py-1.5 text-xs font-semibold text-emerald-700 hover:no-underline">
                            <span className="flex items-center gap-1.5">
                                <UserPlus className="h-3.5 w-3.5" />
                                Dipendenti da creare ({report.to_create.length})
                            </span>
                        </AccordionTrigger>
                        <AccordionContent>
                            <ScrollArea className="max-h-64 rounded border border-slate-200">
                                <div className="divide-y divide-slate-100 text-xs">
                                    {report.to_create.slice(0, 200).map((c, i) => (
                                        <div key={c.matricola || `c-${i}`} className="flex items-center justify-between px-2 py-1.5">
                                            <span className="font-mono-numbers font-semibold text-slate-600">{c.matricola}</span>
                                            <span className="flex-1 px-2 text-slate-800">{c.cognome} {c.nome}</span>
                                        </div>
                                    ))}
                                    {report.to_create.length > 200 && (
                                        <div className="px-2 py-1.5 text-center font-italic text-slate-500">
                                            … +{report.to_create.length - 200} altri
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        </AccordionContent>
                    </AccordionItem>
                )}

                {report.to_update.length > 0 && (
                    <AccordionItem value="update" className="border-b-0">
                        <AccordionTrigger className="py-1.5 text-xs font-semibold text-amber-700 hover:no-underline">
                            <span className="flex items-center gap-1.5">
                                <Pencil className="h-3.5 w-3.5" />
                                Dipendenti da aggiornare ({report.to_update.length})
                            </span>
                        </AccordionTrigger>
                        <AccordionContent>
                            <ScrollArea className="max-h-80 rounded border border-slate-200">
                                <div className="divide-y divide-slate-100 text-xs">
                                    {report.to_update.slice(0, 200).map((u, i) => (
                                        <div key={u.matricola || `u-${i}`} className="px-2 py-1.5">
                                            <div className="mb-1 flex items-center gap-2">
                                                <span className="font-mono-numbers font-semibold text-slate-600">{u.matricola}</span>
                                                <span className="font-semibold text-slate-800">{u.cognome} {u.nome}</span>
                                                <span className="ml-auto rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-800">
                                                    {u.diffs.length} campi
                                                </span>
                                            </div>
                                            <div className="ml-2 space-y-0.5">
                                                {u.diffs.map((d, j) => (
                                                    <div key={d.label || j} className="flex flex-wrap items-baseline gap-1 leading-tight">
                                                        <span className="text-slate-500">{d.label}:</span>
                                                        <span className="rounded bg-red-50 px-1 text-[10px] text-red-700 line-through">
                                                            {d.old ?? "—"}
                                                        </span>
                                                        <span className="text-slate-400">→</span>
                                                        <span className="rounded bg-emerald-50 px-1 text-[10px] font-semibold text-emerald-700">
                                                            {d.new ?? "—"}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    {report.to_update.length > 200 && (
                                        <div className="px-2 py-1.5 text-center font-italic text-slate-500">
                                            … +{report.to_update.length - 200} altri
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        </AccordionContent>
                    </AccordionItem>
                )}

                {report.parsing_errors.length > 0 && (
                    <AccordionItem value="errors" className="border-b-0">
                        <AccordionTrigger className="py-1.5 text-xs font-semibold text-red-700 hover:no-underline">
                            <span className="flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                Avvisi parsing ({report.parsing_errors.length})
                            </span>
                        </AccordionTrigger>
                        <AccordionContent>
                            <ScrollArea className="max-h-40 rounded border border-slate-200">
                                <div className="divide-y divide-slate-100 text-xs">
                                    {report.parsing_errors.slice(0, 100).map((err, i) => (
                                        <div key={`pe-${err.row}-${i}`} className="px-2 py-1">
                                            <span className="font-mono-numbers text-slate-500">Riga {err.row}</span>
                                            {err.matricola && <span className="ml-1 text-slate-500">({err.matricola})</span>}
                                            <span className="ml-1 text-red-700">{err.error}</span>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        </AccordionContent>
                    </AccordionItem>
                )}
            </Accordion>
        </div>
    );
};

const Badge = ({ color, icon, children }) => {
    const map = {
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        amber: "bg-amber-50 text-amber-700 border-amber-200",
        slate: "bg-slate-50 text-slate-700 border-slate-200",
    };
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold ${map[color] || map.slate}`}>
            {icon}
            <span>{children}</span>
        </span>
    );
};
